/**
 * Score Popup Component
 *
 * Animated popup that shows points earned after answering a question.
 */

import { useEffect, useState } from 'react';

interface ScorePopupProps {
  correct: boolean;
  points: number;
  streak: number;
  streakThreshold?: number;
  onAnimationEnd?: () => void;
}

export function ScorePopup({
  correct,
  points,
  streak,
  streakThreshold = 3,
  onAnimationEnd,
}: ScorePopupProps) {
  const [visible, setVisible] = useState(true);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => {
      setScale(1);
    });

    // Animate out after delay
    const timeout = setTimeout(() => {
      setScale(0);
      setVisible(false);
      onAnimationEnd?.();
    }, 2500);

    return () => clearTimeout(timeout);
  }, [onAnimationEnd]);

  if (!visible) return null;

  const showStreak = correct && streak >= streakThreshold;

  return (
    <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
      <div
        className={`transform transition-all duration-300 ease-out ${
          scale === 1 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
        }`}
      >
        <div className="text-center">
          {/* Correct/Wrong indicator */}
          <div
            className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
              correct
                ? 'bg-green-500 shadow-lg shadow-green-500/50'
                : 'bg-red-500 shadow-lg shadow-red-500/50'
            }`}
          >
            {correct ? (
              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>

          {/* Points */}
          <div className={`text-5xl font-bold mb-2 ${correct ? 'text-green-400' : 'text-red-400'}`}>
            {correct ? `+${points}` : '0'}
          </div>

          {/* Message */}
          <div className="text-xl text-white font-medium">
            {correct ? 'Correct!' : 'Wrong!'}
          </div>

          {/* Streak indicator */}
          {showStreak && (
            <div className="mt-4 flex items-center justify-center gap-2 animate-bounce">
              <span className="text-3xl">🔥</span>
              <span className="text-xl font-bold text-orange-400">
                {streak} streak!
              </span>
              <span className="text-3xl">🔥</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScorePopup;
